/**
 * TransportMate Logger
 * ====================
 * Centralised logging with environment-aware verbosity.
 *
 * Behaviour:
 *   DEVELOPMENT  — all levels printed (debug, info, warn, error)
 *   PRODUCTION   — only warn + error printed; debug/info are no-ops
 *
 * Usage:
 *   import { logger } from "@/lib/logger";
 *   logger.debug("[Module]", "verbose detail");   // dev only
 *   logger.info("[Module]", "lifecycle event");    // dev only
 *   logger.warn("[Module]", "recoverable issue");  // always
 *   logger.error("[Module]", "critical failure");  // always
 *
 * RULES:
 *   • NEVER log tokens, passwords, full emails, or session objects
 *   • Prefix every message with [ModuleName] for grep-ability
 *   • Use logger.debug for flow tracing (hidden in prod)
 *   • Use logger.info for significant lifecycle events (hidden in prod)
 *   • Use logger.warn for recoverable issues (visible in prod)
 *   • Use logger.error for failures needing investigation (visible in prod)
 */

const isDev =
  typeof import.meta !== "undefined" &&
  import.meta.env?.DEV === true;

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = (..._args: unknown[]) => {};

export const logger = {
  /** Verbose trace — dev only. Compiled away in production. */
  debug: isDev ? console.log.bind(console) : noop,

  /** Significant lifecycle events — dev only. */
  info: isDev ? console.info.bind(console) : noop,

  /** Recoverable issues — always logged. */
  warn: console.warn.bind(console),

  /** Critical failures — always logged. */
  error: console.error.bind(console),
};
